
import { motion, useAnimate } from 'framer-motion';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { delay } from '../utils/utils';
import { AnimatedCheckMark } from './AnimatedCheckMark';
import { BorderLoaderAnimation } from './BorderLoaderAnimation';
import { Ollie } from './Ollie';

interface OllieToastProps {
    text: string;
    animateOutDelay?: number;
}

export interface OllieToastRef {
    animateIn: (showLoading: boolean, durationSeconds?: number) => Promise<void>;
    animateOut: () => Promise<void>;
}

export const OllieToast = forwardRef<OllieToastRef, OllieToastProps>(({
    text,
    animateOutDelay = 1600
}, ref) => {
    const [scope, animate] = useAnimate();
    const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const isOpen = useRef<boolean>(false);

    useImperativeHandle(ref, () => ({
        animateIn: async (showLoading: boolean = false, durationSeconds: number = 0.5) => {
            if (isOpen.current) return;
            isOpen.current = true;
            if (showLoading) {
                setIsLoading(true);
            }
            await animateFullIn(durationSeconds);
        },
        animateOut: async () => {
            if (!isOpen.current) return;
            await animateFullOut();
            isOpen.current = false;
            setIsLoading(false);
        },
    }));

    const animateClose = async () => {
        await animate("#textContent", { opacity: 0 });
        await animate("#text", { width: "0" })
    };

    const animateSlideIn = async () => {
        await animate(scope.current, { y: 120 }, {
            type: "spring",
            damping: 60,
            stiffness: 1000,
            mass: 2,
        });
    };

    const animateSlideOut = async () => {
        await animate(scope.current, { y: 0 }, {
            type: "spring",
            damping: 60,
            stiffness: 2000,
            mass: 2,
        });
    };

    const animateFullIn = async (seconds: number) => {
        await reset();
        await animateSlideIn();
        await delay(seconds * 1000);
    };

    const animateFullOut = async () => {
        await animateClose();
        if (isLoading) {
            setIsLoadingComplete(true);
            await delay(animateOutDelay);
        }
        await animateSlideOut();
        setIsLoadingComplete(false);
    };

    const reset = async () => {
        const textContentReset = animate("#textContent", { opacity: 1 }, { duration: 0 });
        const textReset = animate("#text", { width: "auto" }, { duration: 0 })
        await Promise.all([textContentReset, textReset])
    }

    return (
        <div ref={scope} className="absolute top-2 left-0 w-full h-[80px] flex justify-center items-center z-50 safe-pt-4 pointer-events-none">
            <motion.div ref={scope} id="container" className="relative max-w-[240px] h-[60px] flex justify-center items-center z-50" initial={{ y: -120 }}>
                <div className="bg-osGrey-4 h-full flex justify-center items-center rounded-[18px]">
                    <div id="avatar" className="h-full flex-shrink-0 flex items-center justify-center p-2">
                        <Ollie type='smile' variant='01' size='xsmall' />
                    </div>
                    <div id="text" className="min-w-[60px] w-auto h-full bg-white flex items-center justify-center rounded-[18px]">
                        {isLoadingComplete ? <AnimatedCheckMark /> : <span id="textContent" className="text-balance font-semibold text-sm px-2">{text}</span>}
                    </div>
                </div>
                <BorderLoaderAnimation isActive={isLoading} isCompleting={isLoadingComplete} className='rounded-[20px]' />
            </motion.div>
        </div>
    );
});

OllieToast.displayName = 'OllieToast';